/* eslint-disable react/no-unused-prop-types */
import * as React from 'react';
import $ from 'jquery';
import '../css/dropdowns.sass';
import DropdownOptionLi from './DropdownOptionLi';
import { HatType } from './Hat';
import NonTextInput from './NonTextInput';
import shouldFlipBG from '../utils/should-flip-bg';

export interface DropdownOption {
  label?: string;
  text: string;
  value: string;
  iconPath?: string;
  additionalText?: string;
}

interface DropdownProps {
  value?: string;
  maxHeight?: string;
  theme?: 'night' | 'day';
  hat?: HatType[];
  label: string;
  ariaLabel?: string;
  noOptionSelectedText?: string;
  name: string;
  options?: DropdownOption[];
  children?: React.ReactElement[];
  id: string;
  required?: boolean;
  readOnly?: boolean;
  focus?: boolean;
  hasError?: boolean;
  errors?: any;
  onChange?: (selectedOption: DropdownOption) => void;
  onComplete?: Function;
  onError?: Function;
  onBlur?: Function;
  onEsc?: Function;
  onEnter?: Function;
  onLeft?: Function;
  onRight?: Function;
  onUp?: Function;
  onDown?: Function;
  onSpace?: Function;
  onClick?: Function;
  expanded?: boolean;
  disabled?: boolean;
}

interface DropdownState {
  focus: boolean;
  expanded: boolean;
  active: boolean;
  activeOptionIndex: number;
  value?: string;
  flipBg: string;
  position: { width: string };
}

export default function Dropdown(props: DropdownProps) {
  const {
    noOptionSelectedText = `Select a ${props.label}`,
    theme = 'day',
    maxHeight = '400px'
  } = props;
  const [options, setOptions] = React.useState<DropdownOption[]>(
    props.options || []
  );

  let selectedOptionIndex = -1;
  options.forEach((opt, index) => {
    if (opt.value === props.value) {
      selectedOptionIndex = index;
    }
  });

  const [state, setState] = React.useState<DropdownState>({
    flipBg: '',
    focus: props.focus || false,
    expanded: props.expanded || false,
    active: false,
    activeOptionIndex: selectedOptionIndex,
    value: props.value,
    position: { width: '0px' }
  });

  React.useEffect(() => {
    setState((prevState: DropdownState) => ({
      ...prevState,
      value: props.value
    }));
  }, [props.value]);

  const dropdownOptionsRef: any = React.useRef();

  React.useEffect(() => {
    if (state.expanded && dropdownOptionsRef.current) {
      dropdownOptionsRef.current.focus();
    }
    if (state.flipBg === '' && shouldFlipBG(dropdownOptionsRef, theme)) {
      setState({
        ...state,
        flipBg: ' flipbg'
      });
    }
  }, [state.expanded]);

  const handleClickOutside = (event: any) => {
    if (state.expanded) {
      if (
        dropdownOptionsRef.current &&
        dropdownOptionsRef.current.contains(event.target)
      ) {
        return;
      }
      setState((prevState: DropdownState) => ({
        ...prevState,
        expanded: false,
        focus: false
      }));
    } else {
      setState((prevState: DropdownState) => ({
        ...prevState,
        focus: false
      }));
    }
  };

  let lastKeyTime = Date.now();
  let buffer: any = [];
  let timer: any = null;

  const selectValueByKeys = () => {
    let i = buffer.length;
    let keyedOption: DropdownOption | undefined;
    while (!keyedOption && i > 0) {
      const keys = buffer.slice(0, i).join('');
      const selectedIndex = options.findIndex(
        (o: DropdownOption) => o.value === state.value
      );
      const startingPoint =
        selectedIndex !== undefined && selectedIndex !== null
          ? selectedIndex + 1
          : 0;
      for (
        let optIndex = startingPoint;
        optIndex < options.length;
        optIndex++
      ) {
        if (
          options[optIndex].text.toLowerCase().startsWith(keys.toLowerCase())
        ) {
          keyedOption = options[optIndex];
          break;
        }
      }
      if (!keyedOption && startingPoint !== 0) {
        for (let optIndex = 0; optIndex < startingPoint; optIndex++) {
          if (
            options[optIndex].text.toLowerCase().startsWith(keys.toLowerCase())
          ) {
            keyedOption = options[optIndex];
            break;
          }
        }
      }
      i -= 1;
    }
    if (keyedOption !== undefined) {
      if (props.onChange !== undefined) {
        props.onChange(keyedOption);
      }
      setState((prevState: DropdownState) => ({
        ...prevState,
        expanded: false,
        focus: true,
        value: keyedOption && keyedOption.value
      }));
    }
  };
  const handleKeyDown = (e: any) => {
    if (timer) {
      clearTimeout(timer);
    }
    if (state.focus) {
      if (e.key === 'Escape') {
        setState((prevState: DropdownState) => ({
          ...prevState,
          expanded: false,
          focus: false
        }));
      } else if (e.key === ' ' || e.key === 'Tab' || e.key === 'Enter') {
        if (state.expanded) {
          if (props.onChange !== undefined) {
            props.onChange(options[state.activeOptionIndex]);
          }
          setState((prevState: DropdownState) => ({
            ...prevState,
            expanded: false,
            value:
              options[state.activeOptionIndex > 0 ? state.activeOptionIndex : 0]
                .value
          }));
        } else if (e.key === 'Tab') {
          setState((prevState: DropdownState) => ({
            ...prevState,
            focus: false
          }));
        } else {
          setState((prevState: DropdownState) => ({
            ...prevState,
            expanded: true
          }));
        }
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        let newIndex = state.activeOptionIndex;
        if (state.expanded) {
          if (e.key === 'ArrowDown') {
            newIndex =
              state.activeOptionIndex === options.length - 1
                ? 0
                : state.activeOptionIndex + 1;
            setState((prevState: DropdownState) => ({
              ...prevState,
              activeOptionIndex: newIndex
            }));
          } else {
            newIndex =
              state.activeOptionIndex === 0
                ? options.length - 1
                : state.activeOptionIndex - 1;
            setState((prevState: DropdownState) => ({
              ...prevState,
              activeOptionIndex: newIndex
            }));
          }
          if (dropdownOptionsRef.current.scrollTo) {
            dropdownOptionsRef.current.scrollTo({
              behavior: 'instant',
              top: dropdownOptionsRef.current.children.item(newIndex).offsetTop
            });
          }
        } else {
          setState((prevState: DropdownState) => ({
            ...prevState,
            expanded: true,
            activeOptionIndex: 0
          }));
        }
      } else {
        timer = setTimeout(selectValueByKeys, 500);
        const currentTime = Date.now();
        if (currentTime - lastKeyTime > 1000) {
          buffer = [];
        }
        buffer.push(e.key);
        lastKeyTime = currentTime;
      }
    }
  };

  React.useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [state]);

  const dropdownOptions = options.map((option, i) => (
    <DropdownOptionLi
      id={`opt${option.value}`}
      selected={state.value === option.value}
      active={state.activeOptionIndex === i}
      iconPath={option.iconPath}
      label={option.label}
      value={option.text}
      additionalText={option.additionalText}
      onSelect={() => {
        setState((prevState: DropdownState) => ({
          ...prevState,
          expanded: false,
          value: options[i].value,
          activeOptionIndex: i
        }));
        if (props.onChange !== undefined) {
          props.onChange(options[i]);
        }
      }}
      theme={theme}
    />
  ));

  const selectedOption: DropdownOption | undefined = options.find(
    (opt) => opt.value === state.value
  );

  let selectedText: string = noOptionSelectedText;
  let selectedLabel: string = props.label;
  let selectedAdditionalText: undefined | string;
  let selectedIcon: undefined | string;
  if (selectedOption !== undefined) {
    selectedText = selectedOption.text;
    if (selectedOption.label) {
      selectedLabel = selectedOption.label;
    }
    selectedIcon = selectedOption.iconPath;
    selectedAdditionalText = selectedOption.additionalText;
  }

  let activeValue = '';
  if (
    options &&
    options.length > state.activeOptionIndex &&
    options[state.activeOptionIndex]
  ) {
    activeValue = options[state.activeOptionIndex].value;
  }

  React.useEffect(() => {
    if (props.options && props.options !== options) {
      setOptions(props.options);
    }
  }, [props.options]);

  React.useEffect(() => {
    if (!props.options && props.children) {
      const newOpts: DropdownOption[] = [];
      props.children.forEach((child) => {
        if ((child.props.text || child.props.label) && child.props.value) {
          newOpts.push({
            text: child.props.text,
            label: child.props.label,
            iconPath: child.props.iconPath,
            value: child.props.value,
            additionalText: child.props.additionalText
          });
        }
      });
      setOptions(newOpts);
    }
  }, []);

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      <div className="dropdown-list">
        <NonTextInput
          ariaLabel={props.ariaLabel}
          readOnly={props.readOnly}
          focus={state.focus}
          unfocus={state.expanded}
          onFocus={() => {
            setState((prevState: DropdownState) => ({
              ...prevState,
              focus: true
            }));
          }}
          setRef={(ll: any) => {
            setState({
              ...state,
              position: {
                width: $(ll.current).css('width')
              }
            });
          }}
          hasError={props.hasError}
          isComplete={state.value !== undefined}
          errors={props.errors}
          theme={props.theme}
          hat={props.hat}
          name={props.name}
          id={props.id}
          onClick={() => {
            setState((prevState: DropdownState) => ({
              ...prevState,
              expanded: !prevState.expanded,
              focus: true
            }));
          }}
          iconPath={selectedIcon}
          label={selectedLabel}
          value={state.value}
          text={selectedText}
          additionalText={selectedAdditionalText}
          disabled={props.disabled}
        >
          {!props.readOnly && (
            <div className={`dropdown__arrow ${theme}`}> </div>
          )}
        </NonTextInput>
        {!props.readOnly && state.expanded && (
          <>
            <div className="popupParentWindow">&nbsp;</div>
            <ul
              style={{
                outline: 'none',
                maxHeight,
                overflow: 'auto',
                width: state.position.width,
                left: 'auto'
              }}
              ref={dropdownOptionsRef}
              id={`uloptions${props.id}`}
              tabIndex={0}
              aria-labelledby={props.id}
              aria-activedescendant={`opt${activeValue}`}
              role="listbox"
              onFocus={() =>
                setState((prevState: DropdownState) => ({
                  ...prevState,
                  focus: true
                }))
              }
              onClick={() => {
                if (props.onClick !== undefined) {
                  props.onClick(state.expanded);
                }
              }}
              onKeyDown={undefined}
              className={`dropdown__list open${state.flipBg} ${theme} `}
            >
              {dropdownOptions}
            </ul>
          </>
        )}
      </div>
    </>
  );
}
